import { t as translate, type LocaleParams } from "@magic-md/i18n";
import { getPlatformService } from "../platform";
import { frontendLog } from "../utils/frontend-logger";
import type { ExecutionProjectionStore } from "./execution-notification-projection";
import { useLogStore } from "./log-store";
import type { NotificationBus } from "./notification-bus";
import { tabSessionRegistry } from "./tab-session-registry";

function tr(key: string, params?: LocaleParams): string {
  return translate("editor", key, params);
}

export interface PublishNotificationHandlerDependencies {
  resyncExecutionSessionProjection: (
    sessionId: string,
    options?: { reconcileContent?: boolean },
  ) => Promise<void>;
}

export function registerPublishNotificationHandlers(
  bus: NotificationBus,
  store: ExecutionProjectionStore,
  dependencies: PublishNotificationHandlerDependencies,
): () => void {
  const disposers: Array<() => void> = [];

  disposers.push(
    bus.registerDomain("event/publish", (method, params) => {
      import("./publish-store")
        .then(({ usePublishStore }) => {
          const publishStore = usePublishStore.getState();
          const logStore = useLogStore.getState();

          switch (method) {
            case "event/publish.started":
              publishStore.handleStarted(
                params as import("@magic-md/engine-client/protocol").PublishStartedEvent,
              );
              logStore.info(
                `[${tr("publish.logPrefix")}] ${tr("publish.log.taskStarted", { taskId: (params as Record<string, unknown>).taskId ?? "?" })}`,
                { source: "engine" },
              );
              break;
            case "event/publish.progress":
              publishStore.handleProgress(
                params as import("@magic-md/engine-client/protocol").PublishProgressEvent,
              );
              break;
            case "event/publish.completed": {
              const event =
                params as import("@magic-md/engine-client/protocol").PublishCompletedEvent;
              publishStore.handleCompleted(event);
              logStore.info(
                `[${tr("publish.logPrefix")}] ${tr("publish.log.taskCompleted", { taskId: event.taskId })}`,
                { source: "engine" },
              );
              break;
            }
            case "event/publish.failed": {
              const event =
                params as import("@magic-md/engine-client/protocol").PublishFailedEvent;
              publishStore.handleFailed(event);
              logStore.error(
                `[${tr("publish.logPrefix")}] ${tr("publish.log.taskFailed", { taskId: event.taskId, message: event.error })}`,
                { source: "engine" },
              );
              break;
            }
            case "event/publish.siteProgress":
              publishStore.handleSiteProgress(
                params as import("@magic-md/engine-client/protocol").PublishSiteProgressEvent,
              );
              break;
            case "event/publish.siteCompleted": {
              const event =
                params as import("@magic-md/engine-client/protocol").PublishSiteCompletedEvent;
              publishStore.handleSiteCompleted(event);
              logStore.info(
                `[${tr("publish.logPrefix")}] ${tr("publish.log.siteCompletedTask", { taskId: event.taskId })}`,
                { source: "engine" },
              );
              break;
            }
            case "event/publish.siteFailed": {
              const event =
                params as import("@magic-md/engine-client/protocol").PublishSiteFailedEvent;
              publishStore.handleSiteFailed(event);
              logStore.error(
                `[${tr("publish.logPrefix")}] ${tr("publish.log.siteFailedTask", { taskId: event.taskId, message: event.error })}`,
                { source: "engine" },
              );
              break;
            }
          }
        })
        .catch((error) => {
          frontendLog.error(
            `[NotificationBus] Failed to load publish-store for ${method}`,
            error,
            { source: "engine" },
          );
        });
    }),
  );

  disposers.push(
    bus.subscribe("event/transport.reconnected", () => {
      const sessionIds = new Set(
        [...tabSessionRegistry.entries()].map(({ sessionId }) => sessionId),
      );
      const activeSessionId = store.getState().sessionId;
      if (activeSessionId) sessionIds.add(activeSessionId);

      if (sessionIds.size > 0) {
        const sessionsToResync = [...sessionIds];
        useLogStore
          .getState()
          .info(
            `Transport reconnected — resyncing ${sessionIds.size} session${sessionIds.size === 1 ? "" : "s"}`,
            { source: "transport" },
          );
        void Promise.allSettled(
          sessionsToResync.map((sessionId) =>
            dependencies.resyncExecutionSessionProjection(sessionId, {
              reconcileContent: true,
            }),
          ),
        ).then((results) => {
          results.forEach((result, index) => {
            if (result.status !== "rejected") return;
            const sessionId = sessionsToResync[index];
            useLogStore
              .getState()
              .warn(
                `Session state resync failed (${sessionId}): ${
                  result.reason instanceof Error
                    ? result.reason.message
                    : String(result.reason)
                }`,
                { source: "transport" },
              );
          });
        });
      }

      import("./publish-store")
        .then(({ usePublishStore }) => {
          const publishStore = usePublishStore.getState();
          const engine = getPlatformService().engine;
          const runningTaskIds = new Set<string>();
          if (
            publishStore.currentTask?.status === "running" &&
            publishStore.currentTask.taskId
          ) {
            runningTaskIds.add(publishStore.currentTask.taskId);
          }
          for (const task of Object.values(publishStore.fileTasks)) {
            if (task.status === "running" && task.taskId) {
              runningTaskIds.add(task.taskId);
            }
          }

          const promises: Promise<void>[] = [];
          for (const taskId of runningTaskIds) {
            promises.push(
              engine.publishGetState({ taskId }).then((state) => {
                if (state.status === "completed" && state.result) {
                  publishStore.handleCompleted({
                    taskId: state.taskId,
                    result: state.result,
                  });
                } else if (state.status === "failed") {
                  publishStore.handleFailed({
                    taskId: state.taskId,
                    error: state.error ?? "unknown error",
                  });
                }
              }),
            );
          }

          for (const task of Object.values(publishStore.folderTasks)) {
            if (task.status !== "running" || !task.taskId) continue;
            promises.push(
              engine
                .publishSiteGetState({ taskId: task.taskId })
                .then((state) => {
                  if (state.status === "completed" && state.result) {
                    publishStore.handleSiteCompleted({
                      taskId: state.taskId,
                      result: state.result,
                    });
                  } else if (state.status === "failed") {
                    publishStore.handleSiteFailed({
                      taskId: state.taskId,
                      error: state.error ?? "unknown error",
                    });
                  }
                }),
            );
          }

          if (
            publishStore.siteTask?.status === "running" &&
            publishStore.siteTask.taskId
          ) {
            promises.push(
              engine
                .publishSiteGetState({
                  taskId: publishStore.siteTask.taskId,
                })
                .then((state) => {
                  if (state.status === "completed" && state.result) {
                    publishStore.handleSiteCompleted({
                      taskId: state.taskId,
                      result: state.result,
                    });
                  } else if (state.status === "failed") {
                    publishStore.handleSiteFailed({
                      taskId: state.taskId,
                      error: state.error ?? "unknown error",
                    });
                  }
                }),
            );
          }

          if (promises.length === 0) return;
          return Promise.allSettled(promises);
        })
        .catch(() => {});
    }),
  );

  return () => {
    for (const dispose of disposers.splice(0)) dispose();
  };
}
